import React, { useCallback, useEffect, useState } from "react";
import { Col, Divider, Form, Input, InputNumber, Row, Select } from "antd";
import { PropertyTabProps } from "js/components/Properties/types";
import {
  filterPropertyAlias,
  handlePasteWithoutAccents,
} from "@/utils/utils";
import { AddressForm } from "js/components/Address";
import type { AddressFormData } from "@/types/address";
import { createEmptyAddressFormData } from "@/types/address";
import {
  formatCurrencyDecimal,
  parseCurrencyDecimal,
} from "@/utils/currencyUtils";

const PropertyTab: React.FC<PropertyTabProps> = ({
  form,
  propertyId,
  propertiesUsages,
}) => {
  const [addressData, setAddressData] = useState<AddressFormData>(
    createEmptyAddressFormData(),
  );
  const [addressTouched, setAddressTouched] = useState<
    Partial<Record<keyof AddressFormData, boolean>>
  >({});

  const handleAddressBlur = useCallback((field: keyof AddressFormData) => {
    setAddressTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  // Sync from Ant Design form to local state (for edit mode pre-population)
  useEffect(() => {
    const values = form.getFieldsValue([
      "street",
      "exterior_number",
      "interior_number",
      "zip_code",
      "neighborhood",
      "city",
      "municipality",
      "state",
    ]);
    if (values.street || values.zip_code) {
      setAddressData({
        street: values.street || "",
        exterior_number: values.exterior_number || "",
        interior_number: values.interior_number || "",
        neighborhood: values.neighborhood || "",
        city: values.city || "",
        municipality: values.municipality || "",
        state: values.state || "",
        zip_code: values.zip_code || "",
      });
    } else {
      setAddressData(createEmptyAddressFormData());
    }
  }, [form, propertyId]);

  const handleAddressChange = useCallback(
    (data: AddressFormData) => {
      setAddressData(data);
      form.setFieldsValue({
        street: data.street,
        exterior_number: data.exterior_number,
        interior_number: data.interior_number,
        neighborhood: data.neighborhood,
        city: data.city,
        municipality: data.municipality,
        state: data.state,
        zip_code: data.zip_code,
      });
    },
    [form],
  );

  return (
    <>
      <Row gutter={[16, 0]} style={{ marginTop: 10 }}>
        <Col xs={24} sm={24} md={24} lg={8}>
          <Form.Item
            name="usage"
            label="Uso que le dará al inmueble"
            rules={[{ required: true, message: "Este campo es requerido" }]}
          >
            <Select
              value={propertyId}
              options={propertiesUsages}
              placeholder="Uso del inmueble"
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={24} lg={8}>
          <Form.Item name="alias" label="Alias del inmueble">
            <Input
              placeholder="Alias del inmueble"
              onChange={(e) => {
                const filtered = filterPropertyAlias(e.target.value);
                form.setFieldValue("alias", filtered);
              }}
              onPaste={(e) => {
                handlePasteWithoutAccents(e, filterPropertyAlias, (value) => {
                  form.setFieldValue("alias", value);
                });
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            name="rental_price"
            label="Valor mensual de la renta"
            rules={[{ required: true, message: "Este campo es requerido" }]}
          >
            <InputNumber<string>
              placeholder="$12,000.00"
              style={{ width: "100%" }}
              formatter={(value) => formatCurrencyDecimal(value ?? "")}
              parser={(value) => parseCurrencyDecimal(value ?? "")}
              addonAfter="MXN"
              stringMode
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            name="maintenance_price"
            label="Valor mensual del mantenimiento"
          >
            <InputNumber<string>
              placeholder="$3,000.00"
              style={{ width: "100%" }}
              formatter={(value) => formatCurrencyDecimal(value ?? "")}
              parser={(value) => parseCurrencyDecimal(value ?? "")}
              addonAfter="MXN"
              stringMode
            />
          </Form.Item>
        </Col>
      </Row>

      <Divider>Dirección</Divider>

      {/* Hidden Form.Items to register address fields with Ant Design validation */}
      <div style={{ display: "none" }}>
        <Form.Item
          name="street"
          rules={[{ required: true, message: "La calle es requerida" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="zip_code"
          rules={[
            { required: true, message: "El código postal es requerido" },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="neighborhood"
          rules={[{ required: true, message: "La colonia es requerida" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="city"
          rules={[{ required: true, message: "La ciudad es requerida" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="state"
          rules={[{ required: true, message: "El estado es requerido" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="exterior_number"
          rules={[{ required: true, message: "El número exterior es requerido" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="municipality"
          rules={[{ required: true, message: "El municipio es requerido" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="interior_number">
          <Input />
        </Form.Item>
      </div>

      <AddressForm
        value={addressData}
        onChange={handleAddressChange}
        onBlur={handleAddressBlur}
        touched={addressTouched}
        showHeader={false}
      />
    </>
  );
};

export default PropertyTab;
